import { useId, useState, type ReactNode } from 'react';
import { RefreshCcw, X } from 'lucide-react';

// 프랜차이즈·매장 관리자 화면이 함께 쓰는 작은 UI 조각.
// 시스템 관리자 화면(features/system-admin/pages.tsx)의 같은 이름 컴포넌트와 클래스를 공유한다.

export interface DataColumn<T> {
  header: string;
  render: (row: T) => ReactNode;
  align?: 'right';
}

export function PageHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <section className="page-heading">
      <div>
        <p className="section-eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </section>
  );
}

export function ActionNotice({
  children,
  tone = 'success',
}: {
  children: ReactNode;
  tone?: 'success' | 'danger' | 'warning';
}) {
  return (
    <div className={`action-notice action-notice--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function StatusBadge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'danger' | 'warning';
}) {
  return <span className={`row-badge row-badge--${tone}`}>{children}</span>;
}

export function FormField({ children, label }: { children: ReactNode; label: string }) {
  return (
    <label className="form-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function FormHelp({ children }: { children: ReactNode }) {
  return <p className="form-help">{children}</p>;
}

export function DataTable<T extends { id: string }>({
  columns,
  emptyLabel,
  rows,
  rowClassName,
}: {
  columns: DataColumn<T>[];
  emptyLabel: string;
  rows: T[];
  rowClassName?: (row: T) => string | undefined;
}) {
  if (rows.length === 0) return <div className="empty-state">{emptyLabel}</div>;
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.header} className={column.align === 'right' ? 'is-right' : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={rowClassName?.(row)}>
              {columns.map((column) => (
                <td key={column.header} className={column.align === 'right' ? 'is-right' : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 조회 상태(로딩/오류/빈 값)를 한 곳에서 처리하고, 데이터가 있을 때만 children을 그린다.
export function QueryPanel<T>({
  children,
  data,
  errorLabel,
  isLoading,
  onRetry,
}: {
  children: (data: T) => ReactNode;
  data: T | undefined;
  errorLabel: string;
  isLoading: boolean;
  onRetry: () => void;
}) {
  if (isLoading) {
    return (
      <section className="data-panel">
        <div className="loading-block">데이터를 불러오는 중입니다.</div>
      </section>
    );
  }
  if (data === undefined) {
    return (
      <section className="data-panel">
        <div className="error-block">
          <strong>{errorLabel}</strong>
          <button className="secondary-button" type="button" onClick={onRetry}>
            <RefreshCcw size={16} />
            다시 시도
          </button>
        </div>
      </section>
    );
  }
  return <>{children(data)}</>;
}

export function ModalFrame({
  children,
  description,
  onClose,
  title,
  wide = false,
}: {
  children: ReactNode;
  description?: string;
  onClose: () => void;
  title: string;
  wide?: boolean;
}) {
  const titleId = useId();
  return (
    <div className="modal-backdrop" role="presentation">
      <section
        className={`modal-panel${wide ? ' modal-panel--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-heading">
          <div>
            <h3 id={titleId}>{title}</h3>
            {description ? <p>{description}</p> : null}
          </div>
          <button className="icon-button" type="button" onClick={onClose}>
            <X size={18} />
            <span className="sr-only">닫기</span>
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}

export function errorMessageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * 제출 전 요약을 보여 주고 확인을 받는 다이얼로그.
 * 처리 중에는 버튼을 잠그고, 서버 오류 메시지는 다이얼로그 안에 그대로 표시한다.
 * onConfirm이 성공하면 부모가 다이얼로그를 닫아야 한다.
 */
export function ConfirmDialog({
  children,
  confirmLabel,
  danger = false,
  description,
  disabled = false,
  onClose,
  onConfirm,
  pendingLabel = '처리 중',
  rows,
  title,
}: {
  children?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  description?: string;
  disabled?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<unknown>;
  pendingLabel?: string;
  rows?: Array<[string, ReactNode]>;
  title: string;
}) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  async function handleConfirm() {
    if (isPending) return;
    setErrorMessage(null);
    setIsPending(true);
    try {
      await onConfirm();
    } catch (error) {
      setErrorMessage(errorMessageOf(error, '요청을 처리하지 못했습니다.'));
      setIsPending(false);
    }
  }

  return (
    <ModalFrame title={title} description={description} onClose={isPending ? () => undefined : onClose}>
      <div className="modal-form">
        {rows ? (
          <dl className="summary-list">
            {rows.map(([label, value]) => (
              <div key={label} style={{ display: 'contents' }}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {children}
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={isPending}>
            취소
          </button>
          <button
            className={danger ? 'danger-button' : 'primary-button'}
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isPending || disabled}
          >
            {isPending ? pendingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </ModalFrame>
  );
}
